import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Mail, Phone, Trash2, MailOpen, Inbox } from "lucide-react";
import { Button } from "./ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { toast } from "sonner";
import { API } from "../App";

const HTTP_UNAUTHORIZED = 401;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { timeZone: "Africa/Nairobi", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export const InquiriesPanel = ({ onLogout, onCountsChange }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleAuthError = useCallback((e) => {
    if (e.response?.status === HTTP_UNAUTHORIZED) {
      toast.error("Session expired. Please login again.");
      onLogout();
    }
  }, [onLogout]);

  const fetchInquiries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/inquiries`);
      setInquiries(res.data);
    } catch (e) {
      handleAuthError(e);
    } finally {
      setLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/admin/inquiries/${id}/read`, {});
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, is_read: true } : i)));
      onCountsChange?.();
    } catch (e) {
      handleAuthError(e);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${deleteTarget.id}`);
      setInquiries((prev) => prev.filter((i) => i.id !== deleteTarget.id));
      onCountsChange?.();
      toast.success("Inquiry deleted");
      setDeleteTarget(null);
    } catch (e) {
      handleAuthError(e);
      toast.error("Failed to delete inquiry");
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="inquiries-loading">
        {Array(4).fill(0).map((_, i) => (
          <div key={i} className="bg-card rounded-xl h-28 animate-pulse border border-border/50" />
        ))}
      </div>
    );
  }

  if (inquiries.length === 0) {
    return (
      <div className="text-center py-12" data-testid="inquiries-empty">
        <Inbox className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="font-heading font-semibold text-foreground mb-2">No inquiries yet</h3>
        <p className="text-muted-foreground text-sm">Leads from the Space Available form will appear here.</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3" data-testid="inquiries-list">
        {inquiries.map((inq) => (
          <div
            key={inq.id}
            data-testid={`inquiry-${inq.id}`}
            className={`bg-card rounded-xl border p-4 ${inq.is_read ? "border-border/50" : "border-primary/40 bg-primary/[0.03]"}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading font-semibold text-foreground">{inq.name}</h3>
                  {!inq.is_read && (
                    <span className="px-2 py-0.5 rounded-full bg-primary text-white text-[10px] font-bold uppercase">New</span>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1 text-sm text-muted-foreground">
                  <a href={`mailto:${inq.email}`} className="flex items-center gap-1 hover:text-primary">
                    <Mail className="w-3.5 h-3.5" /> {inq.email}
                  </a>
                  <a href={`tel:${inq.phone.replace(/\s/g, "")}`} className="flex items-center gap-1 hover:text-primary">
                    <Phone className="w-3.5 h-3.5" /> {inq.phone}
                  </a>
                </div>
              </div>
              <span className="text-xs text-muted-foreground shrink-0">{fmtDate(inq.created_at)}</span>
            </div>
            {inq.message && (
              <p className="text-sm text-foreground mt-3 whitespace-pre-line bg-muted rounded-lg p-3">{inq.message}</p>
            )}
            <div className="flex gap-2 mt-3">
              {!inq.is_read && (
                <Button variant="outline" size="sm" onClick={() => markRead(inq.id)} data-testid={`mark-read-${inq.id}`}>
                  <MailOpen className="w-3.5 h-3.5 mr-1" /> Mark as read
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteTarget(inq)}
                data-testid={`delete-inquiry-${inq.id}`}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Inquiry</AlertDialogTitle>
            <AlertDialogDescription>
              Delete the inquiry from "{deleteTarget?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-inquiry-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
